import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import AdminPageLayout from "../../components/AdminPageLayout";
import { NavigationItem } from "../../types/navigation";
import {
  getAdminDestinations,
  getGroupStyle,
  groupAdminDestinations,
  matchesAdminQuery,
} from "./adminHomeConfig";

const isMacPlatform = (): boolean =>
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.userAgent);

const AdminTile: React.FC<{ item: NavigationItem; onSelect: (path: string) => void }> = ({ item, onSelect }) => {
  const style = getGroupStyle(item.group ?? "");
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(item.path)}
      className={`group relative flex w-full items-start gap-3 overflow-hidden rounded-xl border border-gray-200 bg-white p-3.5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg dark:border-rp-separator dark:bg-rp-surface dark:hover:bg-rp-surface-raised ${style.hover}`}
    >
      <span className={`absolute bottom-3 left-0 top-3 w-[3px] rounded-r opacity-80 transition-opacity group-hover:opacity-100 ${style.spine}`} />
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-transform group-hover:scale-105 ${style.chip}`}>
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-bold leading-tight text-gray-900 dark:text-white">{item.label}</span>
        {item.description && (
          <span className="mt-0.5 block text-xs leading-snug text-gray-500 dark:text-gray-400">{item.description}</span>
        )}
      </span>
    </button>
  );
};

const AdminHomePage: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const destinations = useMemo(() => getAdminDestinations(), []);
  const lanes = useMemo(
    () => groupAdminDestinations(destinations.filter(item => matchesAdminQuery(item, query))),
    [destinations, query]
  );

  // Cmd/Ctrl+K opens the command palette. The hotkey + palette are mounted
  // globally for the whole admin portal via <AdminCommandPaletteHost /> (App
  // scope), so the home page only renders the inline board filter below.

  const handleSelect = (path: string): void => navigate(path);

  return (
    <AdminPageLayout title="Home" description="Jump to any admin tool, or search.">
      <div className="mx-auto max-w-[1480px]">
        {/* Master search bar (inline board filter; Cmd/Ctrl+K opens the palette) */}
        <div className="relative mb-6">
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 transition-all focus-within:border-rp-amber focus-within:ring-1 focus-within:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised">
            <Search className="h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search admin tools..."
              autoComplete="off"
              spellCheck={false}
              aria-label="Search admin tools"
              className="flex-1 bg-transparent text-base text-gray-900 placeholder-gray-400 outline-none dark:text-white dark:placeholder-gray-500"
            />
            <span className="hidden items-center gap-1 font-mono text-[11px] text-gray-400 dark:text-gray-500 sm:flex">
              <kbd className="rounded border border-gray-200 px-1.5 py-0.5 dark:border-rp-separator">{isMacPlatform() ? "Cmd" : "Ctrl"}</kbd>
              <kbd className="rounded border border-gray-200 px-1.5 py-0.5 dark:border-rp-separator">K</kbd>
            </span>
          </div>
        </div>

        {lanes.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white py-24 text-center dark:border-rp-separator dark:bg-rp-surface">
            <p className="text-base font-semibold text-gray-700 dark:text-gray-300">No tools match that search</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Try a different term, or clear the search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
            {lanes.map(lane => {
              const style = getGroupStyle(lane.group);
              return (
                <section
                  key={lane.group}
                  className="rounded-2xl border border-gray-200 bg-gray-50 p-3 dark:border-rp-separator dark:bg-rp-surface-sunken"
                >
                  <header className="mb-3 flex items-center gap-2 px-1">
                    <span className={`h-2 w-2 rounded-full ${style.dot}`} />
                    <h2 className={`text-[11px] font-bold uppercase tracking-wider ${style.text}`}>{lane.group}</h2>
                    <span className={`ml-auto rounded-full border border-current px-1.5 font-mono text-[10px] ${style.text}`}>
                      {lane.items.length}
                    </span>
                  </header>
                  <div className="flex flex-col gap-2">
                    {lane.items.map(item => (
                      <AdminTile key={item.id} item={item} onSelect={handleSelect} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </AdminPageLayout>
  );
};

export default AdminHomePage;
